import { Node, Point } from '../services/edgeApi';
import { isValidNumber } from './numberUtils';

export const colors = {
  matteRed: '#F05E4B',
  matteGreen: '#2DC963',
  matteYellow: '#E6E657',
  matteBlue: '#19538C',
  mattePurple: '#653083',
  primaryBlue: '#0D6EFD',
  lightGrey: '#E5E7EB',
  grey: '#6B7280',
  darkGrey: '#2c2c2c',
  dark: '#111311',

  map: {
    dark: {
      countyStroke: '#2c2c2c',
      countyFill: '#0e0e0e',
      countyLabelStroke: '#2c2c2c',
      countyLabelFill: '#E5E7EB',
    },
    light: {
      countyStroke: '#e5e7e5',
      countyFill: '#fafaf8',
      countyLabelStroke: '#3f3f3f',
      countyLabelFill: '#E5E7EB',
    },
  },

  elements: {
    interconnectColor: '#00BFFF',
    disabledInterconnectColor: '#00BFFF80',
    receiptNode: '#ED0907',
    deliveryNode: '#0605EB',
    bidirectionalNode: '#BE50DB',
    invalidMarketColor: '#6D6D6D',
    minMarketColor: '#0096FF',
    maxMarketColor: '#FF0000',
    buyMarketColor: '#FF0000',
    sellMarketColor: '#0096FF',
    bothMarketColor: '#BE50DB',
    verifiedCompressorBorder: '#2D8D00',
    unverifiedCompressorBorder: '#790800',
    contractPath: {
      IN_PATH: '#FFFF80FF',
      OUT_OF_PATH: '#80FF95FF',
      IT: '#80D4FFFF',
    },
  },

  themes: {
    dark: {
      badgeBackground: '#212629',
      labelColor: '#d7dfcdff',
      labelBackground: '#000000cc',
      body: '#191b1d',
      color: '#d7dfcdff',
      secondaryBg: '#212629',
    },
    light: {
      badgeBackground: '#D3D3D3',
      labelColor: '#111311ff',
      labelBackground: '#ffffffcc',
      body: '#ffffff',
      color: '#111311ff',
      secondaryBg: '#f8f9fa',
    },
  },
};

export type RGB = {
  r: number;
  g: number;
  b: number;
  a?: number;
};

/**
 *
 * @param {string} hex hex color string starting with #
 * @returns {boolean} Returns true if the hex is valid
 */
export const isValidHex = (hex: string) => {
  if (!hex || typeof hex !== 'string') return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/.test(hex);
};

export const isValidRGB = (rgb: string) => {
  if (!rgb || typeof rgb !== 'string') return false;
  return /^rgb(a)?\((\s*\d+\s*,){2,3}\s*[\d.]+\s*\)$/.test(rgb);
};

export const isValidHSL = (hsl: string) => {
  if (!hsl || typeof hsl !== 'string') return false;
  return /^hsl(a)?\((\s*\d+\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,?\s*[\d.]*\s*)\)$/.test(
    hsl,
  );
};

/**
 *
 * @param color the color string to infer the format
 * @returns {string} Returns 'hex', 'rgb', 'hsl', or 'unknown'
 */
export const getColorFormat = (color: string) => {
  if (isValidHex(color)) {
    return 'hex';
  }
  if (isValidRGB(color)) {
    return 'rgb';
  }
  if (isValidHSL(color)) {
    return 'hsl';
  }
  return 'unknown';
};

/**
 *
 * @param {string} color hex color string starting with #
 * @returns {string} Returns either #000000 or #FFFFFF depending on the contrast of the color
 */
export const getContrastColor = (color: string) => {
  if (!color) return 'currentColor';
  const hexColor = toHex(color);
  if (!hexColor) return 'currentColor';

  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 186 ? '#000000' : '#FFFFFF';
};

/**
 *
 * @returns {string} Returns a color based on the key
 */
const colorKey = (key: number | string) => {
  if (typeof key === 'number') return key;

  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
};

export const generateColor = (
  key: number | string,
  saturation: number = 50,
  luminosity: number = 80,
) => {
  return `hsl(${(100 * colorKey(key)) % 361},${saturation}%,${luminosity}%)`;
};

/**
 * Converts a hex color to rgb
 * @returns {RGB | null} Returns an object with r, g, and b values
 */
export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(
    hex,
  );
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: result[4] ? parseInt(result[4], 16) / 255 : 1,
      }
    : null;
};

/**
 * Converts an rgb color to hex
 * @param {RGB} rgb
 * @returns {string} Returns a hex color string
 */
export const rgbToHex = (rgb: RGB) => {
  return (
    '#' +
    ((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b)
      .toString(16)
      .slice(1)
      .toUpperCase()
  );
};

const hueToRGB = (p: number, q: number, t: number) => {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
};

/**
 *
 * @param {string} hsl string in format 'hsl(0, 0%, 0%)'
 * @param {number} opacity opacity value between 0 and 1
 * @returns {RGB} Returns an object with r, g, and b values
 */
export const hslToRgb = (hsl: string, opacity: number) => {
  const hslValues = hsl.match(/\d+/g);
  if (hslValues === null || hslValues.length < 3) {
    throw new Error('Invalid HSL color format');
  }
  const h = Number(hslValues[0] ?? 0) / 360;
  const s = Number(hslValues[1] ?? 0) / 100;
  const l = Number(hslValues[2] ?? 0) / 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRGB(p, q, h + 1 / 3);
    g = hueToRGB(p, q, h);
    b = hueToRGB(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    a: opacity,
  };
};

export const rgb255ToRgb1 = (rgb: RGB) => {
  return {
    r: rgb.r / 255,
    g: rgb.g / 255,
    b: rgb.b / 255,
    a: rgb.a,
  };
};

export const getNodeColor = (
  node: Node,
  points: { [key: string]: Point },
  activelySelectedNodeIds: number[],
) => {
  const pointsDirFlo = node.points.map((pointId) => points[pointId]?.dir_flo);
  let color;
  const isActiveNode = activelySelectedNodeIds.includes(Number(node.id));

  if (isActiveNode) {
    color = '#5AAB61';
  } else {
    let nodeDirFlo;
    if (
      pointsDirFlo.includes('B') ||
      (pointsDirFlo.includes('R') && pointsDirFlo.includes('D'))
    ) {
      nodeDirFlo = 'B';
    } else if (pointsDirFlo.includes('D')) {
      nodeDirFlo = 'D';
    } else if (pointsDirFlo.includes('R')) {
      nodeDirFlo = 'R';
    }

    switch (nodeDirFlo) {
      case 'R':
        color = colors.elements.receiptNode;
        break;
      case 'D':
        color = colors.elements.deliveryNode;
        break;
      case 'B':
        color = colors.elements.bidirectionalNode;
        break;
      default:
        color = colors.matteBlue;
    }
  }
  return color;
};

export const interpolateColor = (
  c0: string = '#000000',
  c1: string = '#000000',
  f: number,
) => {
  if (
    isValidNumber(f) === false ||
    isValidHex(c0) === false ||
    isValidHex(c1) === false
  ) {
    return '#ffffff00';
  }
  const saturatedF = Math.min(Math.max(f, 0), 1);
  if (c0.startsWith('#')) {
    c0 = c0.slice(1);
  }
  if (c1.startsWith('#')) {
    c1 = c1.slice(1);
  }
  const _c0: number[] = c0
    .match(/.{1,2}/g)
    ?.map((oct) => parseInt(oct, 16) * (1 - saturatedF)) ?? [0, 0, 0];
  const _c1: number[] = c1
    .match(/.{1,2}/g)
    ?.map((oct) => parseInt(oct, 16) * saturatedF) ?? [0, 0, 0];
  const ci = [0, 1, 2].map((i) => Math.min(Math.round(_c0[i] + _c1[i]), 255));
  const rgb = { r: ci[0] || 0, g: ci[1] || 0, b: ci[2] || 0 };
  const hex = rgbToHex(rgb);
  return hex;
};

/**
 *
 * @param color color string (in hex, rgb, or hsl format) to turn into HEX
 * @returns HEX representation of the color, does not preserve opacity
 */
export const toHex = (color: string) => {
  const format = getColorFormat(color);
  if (format === 'hex') {
    return color;
  } else if (format === 'rgb') {
    const rgbValues = color
      .replace(/rgba?\(/, '')
      .replace(')', '')
      .split(',')
      .map((v) => parseInt(v.trim(), 10));
    if (rgbValues.length >= 3) {
      const rgb: RGB = { r: rgbValues[0], g: rgbValues[1], b: rgbValues[2] };
      return rgbToHex(rgb);
    }
    return color;
  } else if (format === 'hsl') {
    const rgb = hslToRgb(color, 1);
    return rgbToHex({ r: rgb.r, g: rgb.g, b: rgb.b });
  }
  return null;
};

/**
 *
 * @param color the color string (in hex, rgb, or hsl format) to turn into RGB
 * @returns RGB representation of the color, does not preserve opacity
 */
export const toRGB = (color: string) => {
  const format = getColorFormat(color);
  if (format === 'rgb') {
    return color;
  } else if (format === 'hex') {
    const rgb = hexToRgb(color);
    if (rgb) {
      return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    }
    return color;
  } else if (format === 'hsl') {
    const rgb = hslToRgb(color, 1);
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  }
  return null;
};

/**
 *
 * @param color the color string (in hex, rgb, or hsl format) to turn into HSL
 * @returns HSL representation of the color, does not preserve opacity
 */
export const toHSL = (color: string) => {
  const format = getColorFormat(color);
  if (format === 'hsl') {
    return color;
  }
  const rgbColor = toRGB(color);
  if (rgbColor) {
    const rgbValues = rgbColor
      .replace(/rgba?\(/, '')
      .replace(')', '')
      .split(',')
      .map((v) => parseInt(v.trim(), 10));
    if (rgbValues.length >= 3) {
      const r = rgbValues[0] / 255;
      const g = rgbValues[1] / 255;
      const b = rgbValues[2] / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0,
        s = 0,
        l = (max + min) / 2;

      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(
        l * 100,
      )}%)`;
    }
    return color;
  }
  return null;
};

/**
 *
 * @param color color string (in hex, rgb, or hsl format)
 * @param opacity opacity value between 0 and 1
 * @returns modified color string with the new opacity
 */
export const setOpacity = (color: string, opacity: number) => {
  const format = getColorFormat(color);
  if (format === 'hex') {
    const rgb = hexToRgb(color);
    if (rgb) {
      rgb.a = Math.round(opacity * 255);
      return rgbToHex(rgb);
    }
    return color;
  } else if (format === 'rgb') {
    const rgbValues = color
      .replace(/rgba?\(/, '')
      .replace(')', '')
      .split(',')
      .map((v) => v.trim());
    if (rgbValues.length >= 3) {
      return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${opacity})`;
    }
    return color;
  } else if (format === 'hsl') {
    const hslValues = color
      .replace(/hsla?\(/, '')
      .replace(')', '')
      .split(',')
      .map((v) => v.trim());
    if (hslValues.length >= 3) {
      return `hsla(${hslValues[0]}, ${hslValues[1]}, ${hslValues[2]}, ${opacity})`;
    }
    return color;
  }
  return color;
};

/**
 *
 * @param color the color string (in hex, rgb, or hsl format)
 * @param saturation saturation value between 0 and 100
 * @returns modified color string with the new saturation
 */
export const setSaturation = (color: string, saturation: number) => {
  const format = getColorFormat(color);
  const hsl = toHSL(color);
  if (hsl) {
    const hslValues = hsl
      .replace(/hsla?\(/, '')
      .replace(')', '')
      .split(',')
      .map((v) => v.trim());
    if (hslValues.length >= 3) {
      const newHsl = `hsl(${hslValues[0]}, ${saturation}%, ${hslValues[2]})`;
      switch (format) {
        case 'hex':
          return toHex(newHsl) || color;
        case 'rgb':
          return toRGB(newHsl) || color;
        case 'hsl':
          return newHsl;
      }
    }
    return color;
  }
  return color;
};

/**
 *
 * @param color the color string (in hex, rgb, or hsl format)
 * @param luminosity luminosity value between 0 and 100
 * @returns modified color string with the new luminosity
 */
export const setLuminosity = (color: string, luminosity: number) => {
  const format = getColorFormat(color);
  const hsl = toHSL(color);
  if (hsl) {
    const hslValues = hsl
      .replace(/hsla?\(/, '')
      .replace(')', '')
      .split(',')
      .map((v) => v.trim());
    if (hslValues.length >= 3) {
      const newHsl = `hsl(${hslValues[0]}, ${hslValues[1]}, ${luminosity}%)`;
      switch (format) {
        case 'hex':
          return toHex(newHsl) || color;
        case 'rgb':
          return toRGB(newHsl) || color;
        case 'hsl':
          return newHsl;
      }
    }
    return color;
  }
  return color;
};
